import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { SparklesIcon, ClipboardListIcon, MessageCircleIcon, ChevronDownIcon, PlayCircleIcon, ArrowRightIcon } from 'lucide-react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
const InterfaceCard = ({
  icon,
  title,
  description,
  isFeatured,
  badge,
  demoUrl,
  demoImage,
  index
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.div ref={ref} className={`bg-white rounded-2xl border shadow-sm overflow-hidden relative ${isFeatured ? 'border-orange-500 ring-2 ring-orange-200 col-span-full' : 'border-gray-200'}`} initial={{
    opacity: 0,
    y: 40
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 40
  }} transition={{
    duration: 0.6,
    delay: index * 0.15,
    ease: 'easeOut'
  }} data-id="element-241">
      <div className={`p-5 sm:p-6 md:p-8 ${isFeatured ? 'flex flex-col md:flex-row md:gap-8 md:items-start' : ''}`} data-id="element-242">
        {badge && <div className="absolute top-3 sm:top-4 right-3 sm:right-4 z-10" data-id="element-243">
            <span className="bg-orange-500 text-white text-xs font-semibold px-2.5 sm:px-3 py-1 rounded-full flex items-center gap-1" data-id="element-244">
              <SparklesIcon size={12} data-id="element-245" />
              {badge}
            </span>
          </div>}

        <div className={isFeatured ? 'md:flex-1' : ''} data-id="element-246">
          <motion.div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 ${isFeatured ? 'bg-orange-200' : 'bg-orange-100'}`} initial={{
          scale: 0,
          rotate: -180
        }} animate={isInView ? {
          scale: 1,
          rotate: 0
        } : {
          scale: 0,
          rotate: -180
        }} transition={{
          duration: 0.5,
          delay: index * 0.15 + 0.2,
          ease: 'easeOut'
        }} data-id="element-247">
            {icon}
          </motion.div>
          <h3 className={`font-bold text-gray-900 mb-2 sm:mb-3 ${isFeatured ? 'text-xl sm:text-2xl' : 'text-lg sm:text-xl'}`} data-id="element-248">
            {title}
          </h3>
          <p className={`text-gray-600 leading-relaxed ${isFeatured ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`} data-id="element-249">
            {description}
          </p>
        </div>

        {isFeatured && demoImage && <div className="md:flex-1 mt-6 md:mt-0" data-id="element-250">
            <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-md" data-id="element-251">
              <img src={demoImage} alt={`${title} preview`} className="w-full h-auto" loading="lazy" width="600" height="400" data-id="element-252" />
            </div>
          </div>}
      </div>

      {demoUrl && <div className="border-t border-gray-200" data-id="element-253">
          <button onClick={() => setIsExpanded(!isExpanded)} className="w-full px-5 sm:px-8 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50 transition-colors min-h-[44px]" data-id="element-254">
            <span className="flex items-center gap-2 text-orange-500 font-medium text-sm sm:text-base" data-id="element-255">
              <PlayCircleIcon size={18} className="sm:w-5 sm:h-5" data-id="element-256" />
              See in action
            </span>
            <motion.div animate={{
          rotate: isExpanded ? 180 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-257">
              <ChevronDownIcon size={18} className="text-gray-400 sm:w-5 sm:h-5" data-id="element-258" />
            </motion.div>
          </button>

          <AnimatePresence data-id="element-259">
            {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-260">
                <div className="px-4 sm:px-8 pb-4 sm:pb-8" data-id="element-261">
                  <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-lg bg-white" data-id="element-262">
                    {demoUrl.includes('business-onboarding-form') ? <iframe src={demoUrl} className="w-full border-0 block" style={{
                background: 'transparent',
                minHeight: '800px',
                overflow: 'hidden'
              }} title={`${title} Demo`} frameBorder="0" scrolling="no" allowTransparency={true} loading="lazy" data-id="element-263" /> : <div className="aspect-video" data-id="element-264">
                        <iframe src={demoUrl} className="w-full h-full" frameBorder="0" allowFullScreen title={`${title} Demo`} loading="lazy" data-id="element-265" />
                      </div>}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>}
    </motion.div>;
};
const Interfaces = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  // Load the FluidForm navigation script
  useEffect(() => {
    const existingScript = document.querySelector('script[src="https://app.fluid-form.ai/fluid-navigate.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://app.fluid-form.ai/fluid-navigate.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  const interfaces = [{
    icon: <SparklesIcon size={28} className="text-orange-500" data-id="element-266" />,
    title: 'Signature FluidForm',
    description: 'A hybrid chat + form experience that feels human. Users can answer naturally, upload documents, and ask questions — while FluidForm keeps the submission structured and validated.',
    isFeatured: true,
    badge: 'Featured',
    demoUrl: 'https://app.fluid-form.ai/ff/org_370FLDDfGS3A3yaESUAAv8eTIQ5/business-onboarding-form?sig=org_370FLDDfGS3A3yaESUAAv8eTIQ5-business-onboarding-form-dd5ac6a9e0435f1e&mode=headless&chatPath=chat-router',
    demoImage: 'https://cdn.magicpatterns.com/uploads/fMkpsYN5BAYrdzUeLz9vky/SignatureForm.gif'
  }, {
    icon: <ClipboardListIcon size={28} className="text-orange-500" data-id="element-267" />,
    title: 'Classic Forms',
    description: 'Single-page or multi-step forms with multi-modal inputs and AI validation built in.',
    isFeatured: false
  }, {
    icon: <MessageCircleIcon size={28} className="text-orange-500" data-id="element-268" />,
    title: 'Conversational Forms',
    description: "A chat-first experience that doesn't feel like a form — but still produces complete, schema-valid submissions.",
    isFeatured: false
  }];
  return <section ref={ref} className="w-full bg-gray-50 py-12 sm:py-16 md:py-20 lg:py-[5.75rem]" id="interfaces" data-id="element-269">
      <div className="container mx-auto px-4" data-id="element-270">
        <motion.div className="text-center mb-10 sm:mb-12 md:mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-271">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6" data-id="element-272">
            Adaptable Form Experiences
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-273">
            Pick the UX that matches your funnel — without changing the data you
            collect.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto mb-10 sm:mb-12 md:mb-16" data-id="element-274">
          {interfaces.map((item, index) => <InterfaceCard key={index} {...item} index={index} data-id="element-275" />)}
        </div>

        <motion.div className="text-center" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6,
        delay: 0.4,
        ease: 'easeOut'
      }} data-id="element-276">
          <motion.a href="https://accounts.fluid-form.ai/sign-up" target="_blank" onClick={() => window.gtag('event', 'signup_click', {
          source: 'section_interfaces'
        })} rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-orange-500 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-medium hover:bg-orange-600 transition-colors shadow-lg hover:shadow-orange-500/25 min-h-[44px] w-full sm:w-auto justify-center" whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} data-id="element-277">
            Try it now
            <ArrowRightIcon size={18} className="sm:w-5 sm:h-5" data-id="element-278" />
          </motion.a>
        </motion.div>
      </div>
    </section>;
};
export default Interfaces;